import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Checkbox, Form, Input, message, Select, notification } from 'antd';
import {
  checkRegistrationEmail,
  registerMember,
  REGISTRATION_EMAIL_STATUS,
  resendActivationLinkByEmail,
  subscribeToNewsletter,
} from "../actions/userProfileAction";
import { getBrandId, getLanguage } from '../selectors/global';
import { filterSelectByLabel } from '../helpers/select-filter';
import { tokenRegistration } from "../selectors/authentication";
import { showModal } from '../actions/modalsActions';

import Btn from '../components/common/btn';
import DateOfBirthField from './DateOfBirthField';
import { toSubmitDate } from '../validators/date-validator';
import ModalActivationLinkSent from '../components/ModalActivationLinkSent';
import ContactAssistance from '../components/common/ContactAssistance';
import './register-form.scss';

const SUPPORT_EMAIL = 'sandos4u.mx@sandos.com';
const EMAIL_CHECK_DEBOUNCE_MS = 400;
const BLOCKING_EMAIL_STATUSES = [
  REGISTRATION_EMAIL_STATUS.INVALID_EMAIL,
  REGISTRATION_EMAIL_STATUS.PENDING_VERIFICATION,
  REGISTRATION_EMAIL_STATUS.UNSUBSCRIBED,
  REGISTRATION_EMAIL_STATUS.ACTIVE,
  REGISTRATION_EMAIL_STATUS.BLOCKED,
];

const countriesLangES = [
  278, // AR = Argentina
  308, // BO = Bolivia
  280, // CL = Chile
  281, // CO = Colombia
  298, // CR = Costa Rica
  286, // CU = Cuba
  274, // DO = República Dominicana
  297, // EC = Ecuador
  254, // GT = Guatemala
  413, // HN = Honduras
  277, // MX = México
  378, // NI = Nicaragua
  234, // PA = Panama
  309, // PY = Paraguay
  291, // PE = Perú
  283, // UY = Uruguay
  282, // Ve = Venezuela
];

class RegisterForm extends React.Component {
  state = {
    selectedItems: [],
    isSending: false,
    emailStatus: null,
    loyaltyMemberId: null,
    emailCheckLoading: false,
    emailCheckError: null,
    isResending: false,
    showLinkSentModal: false,
  };

  emailCheckTimer = null;
  emailCheckRequestId = 0;

  componentWillUnmount() {
    if (this.emailCheckTimer) {
      clearTimeout(this.emailCheckTimer);
    }
  }

  isEmailFormatValid = (email) => {
    if (!email || !email.trim()) {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  resetEmailStatus = () => {
    this.setState({
      emailStatus: null,
      loyaltyMemberId: null,
      emailCheckError: null,
      emailCheckLoading: false,
    });
  };

  runEmailStatusCheck = (email) => {
    if (!this.isEmailFormatValid(email)) {
      this.resetEmailStatus();
      return;
    }

    const requestId = ++this.emailCheckRequestId;
    this.setState({ emailCheckLoading: true, emailCheckError: null });

    this.props.checkRegistrationEmail(email)
      .then((result) => {
        if (requestId !== this.emailCheckRequestId) {
          return;
        }
        this.setState({
          emailStatus: result.status,
          loyaltyMemberId: result.loyaltyMemberId,
          emailCheckLoading: false,
          emailCheckError: null,
        });
      })
      .catch((err) => {
        if (requestId !== this.emailCheckRequestId) {
          return;
        }
        this.setState({
          emailStatus: null,
          loyaltyMemberId: null,
          emailCheckLoading: false,
          emailCheckError: err.message || 'Error checking email',
        });
      });
  };

  scheduleEmailStatusCheck = (email) => {
    if (this.emailCheckTimer) {
      clearTimeout(this.emailCheckTimer);
    }
    this.emailCheckTimer = setTimeout(() => {
      this.runEmailStatusCheck(email);
    }, EMAIL_CHECK_DEBOUNCE_MS);
  };

  handleEmailChange = (e) => {
    this.scheduleEmailStatusCheck(e.target.value);
    if (this.props.form.getFieldValue('confirmEmail')) {
      this.props.form.validateFields(['confirmEmail']);
    }
  };

  handleResendPendingVerification = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.state.emailStatus !== REGISTRATION_EMAIL_STATUS.PENDING_VERIFICATION) {
      return;
    }
    this.resendActivationByEmailAndGoToLogin();
  };

  handleResendUnsubscribedVerification = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.state.emailStatus !== REGISTRATION_EMAIL_STATUS.UNSUBSCRIBED) {
      return;
    }
    this.resendActivationByEmailAndGoToLogin();
  };

  resendActivationByEmailAndGoToLogin = () => {
    const email = (this.props.form.getFieldValue('email') || '').trim();
    if (!email) {
      message.error(this.context.t('Failed to resend verification email'));
      return;
    }
    this.setState({ isResending: true });
    this.props.resendActivationLinkByEmail(email)
      .then(() => {
        this.setState({ isResending: false, showLinkSentModal: true });
      })
      .catch((err) => {
        message.error(err.message || this.context.t('Failed to resend verification email'));
        this.setState({ isResending: false });
      });
  };

  handleCloseLinkSentModal = () => {
    this.setState({ showLinkSentModal: false });
    this.props.history.push('/');
  };

  handleOpenLogin = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.props.showLoginModal();
  };

  handleGoToForgotPassword = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const email = this.props.form.getFieldValue('email');
    this.props.history.push({
      pathname: '/forgot-password',
      state: { email },
    });
  };

  isSubmitBlockedByEmailStatus = () => {
    return BLOCKING_EMAIL_STATUSES.indexOf(this.state.emailStatus) !== -1;
  };

  getEmailValidateStatus = () => {
    const { emailStatus, emailCheckLoading, emailCheckError } = this.state;

    if (emailCheckLoading) {
      return 'validating';
    }
    if (emailCheckError || this.isSubmitBlockedByEmailStatus()) {
      return 'error';
    }
    if (emailStatus === REGISTRATION_EMAIL_STATUS.NOT_FOUND) {
      return 'success';
    }
    return undefined;
  };

  renderEmailStatusHelp = () => {
    const { emailStatus, emailCheckLoading, emailCheckError, isResending } = this.state;
    const t = this.context.t;

    if (emailCheckLoading) {
      return <p className="text-sm text-gray-500 mt-1">{t('Checking email...')}</p>;
    }

    if (emailCheckError) {
      return <p className="text-sm text-red-500 mt-1">{t('Could not verify this email. Please try again.')}</p>;
    }

    if (emailStatus === REGISTRATION_EMAIL_STATUS.INVALID_EMAIL) {
      return (
        <div className="mt-1 text-sm">
          <p>{t('Please enter a valid email address')}</p>
          <p>{t('Check the email spelling and try again.')}</p>
        </div>
      );
    }

    if (emailStatus === REGISTRATION_EMAIL_STATUS.NOT_FOUND) {
      return (
        <div className="email-status-help mt-1 text-sm">
          <p className="email-status-success text-green-500">
            {t('Valid email, you can register your account with this email.')}
          </p>
        </div>
      );
    }

    if (emailStatus === REGISTRATION_EMAIL_STATUS.PENDING_VERIFICATION) {
      return (
        <div className="mt-1 text-sm">
          <p className="text-red-500">
            {t('This email has a registration in progress.')}{' '}
            <Btn
              className="text-main hover:text-highlight focus:outline-none p-0 inline"
              onClick={this.handleResendPendingVerification}
              disabled={isResending}
            >
              {isResending ? t('Sending') : t('Click to receive verification email.')}
            </Btn>
          </p>
        </div>
      );
    }

    if (emailStatus === REGISTRATION_EMAIL_STATUS.UNSUBSCRIBED) {
      return (
        <div className="email-status-help mt-1 text-sm">
          <p className="text-red-500">
            {t('This account was deactivated.')}{' '}
            <Btn
              className="email-status-cta focus:outline-none p-0 inline"
              onClick={this.handleResendUnsubscribedVerification}
              disabled={isResending}
            >
              {isResending ? t('Sending') : t('Click to receive a new verification email and reactivate it.')}
            </Btn>
          </p>
        </div>
      );
    }

    if (emailStatus === REGISTRATION_EMAIL_STATUS.ACTIVE) {
      return (
        <div className="mt-1 text-sm">
          <p className="text-red-500">
            {t('This email is already registered with an active account.')}{' '}
            <Btn className="text-main hover:text-highlight focus:outline-none p-0 inline" onClick={this.handleOpenLogin}>
              {t('Click to log in')}
            </Btn>
            <span className="mx-1 text-red-500">{t('or')}</span>
            <Btn className="text-main hover:text-highlight focus:outline-none p-0 inline" onClick={this.handleGoToForgotPassword}>
              {t('Click to receive password reset instructions')}
            </Btn>
          </p>
        </div>
      );
    }

    if (emailStatus === REGISTRATION_EMAIL_STATUS.BLOCKED) {
      return (
        <div className="email-status-help mt-1 text-sm">
          <p>
            {t('We were unable to sign in to your account. Please contact our Support team at')}{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="email-status-cta">
              {SUPPORT_EMAIL}
            </a>{' '}
            {t('to receive assistance.')}
          </p>
        </div>
      );
    }

    return null;
  };

  onRegisterSuccess = () => {
    notification.success({
      message: this.context.t('We sent you a confirmation email with a link to activate your account.'),
      duration: 10,
    });
    this.setState({ isSending: false });
    this.props.onBack();
  }

  getRegistrationErrorContent = (errorPayload = {}) => {
    const t = this.context.t;
    const message = (errorPayload.message || '').trim();
    const normalized = message.toLowerCase();

    if (normalized === 'user already created') {
      return (
        <div>
          <p>{t('The email is already registered')}</p>
          <p>
            <Btn className="text-main hover:text-highlight focus:outline-none p-0" onClick={this.handleOpenLogin}>
              {t('Log-in')}
            </Btn>
            <span className="mx-1">{t('or')}</span>
            <Btn className="text-main hover:text-highlight focus:outline-none p-0" onClick={this.handleGoToForgotPassword}>
              {t('Forgot your password?')}
            </Btn>
          </p>
        </div>
      );
    }

    if (normalized === 'please enter valid email id') {
      return (
        <div>
          <p>{t('Please enter a valid email address')}</p>
          <p>{t('Check the email spelling and try again.')}</p>
        </div>
      );
    }

    if (normalized === 'the email service is currently not available') {
      return (
        <div>
          <p>{t('Your registration was received, but we could not send the confirmation email.')}</p>
          <p>{t('Please try again later or contact Support.')}</p>
          <p>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-main hover:text-highlight">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      );
    }

    if (normalized === 'missing or invalid token') {
      return <p>{t('We could not complete your registration due to an authentication error. Please try again.')}</p>;
    }

    if (normalized === 'merge request already exists!') {
      return <p>{t('A merge request already exists for this account. Please contact Support.')}</p>;
    }

    if (normalized === 'error saving the member data' || !message) {
      return <p>{t('We could not save your registration. Please review your data and try again.')}</p>;
    }

    // Fallback: show backend message as clearly as possible
    return <p>{t(message)}</p>;
  };

  onRegisterError = (errorPayload) => {
    this.setState({ isSending: false });
    notification.error({
      message: this.getRegistrationErrorContent(errorPayload),
      duration: 12,
    });
  }

  subscribeNewsletter = (formValues) => {
    if (!formValues.promotions && !formValues.isPromotions) {
      return;
    }
    if (!this.props.registrationInfo || !this.props.registrationInfo.countryCodeTo) {
      return;
    }
    const country = this.props.registrationInfo.countryCodeTo.find((c) => c.countryId === formValues.country);
    if (!country) {
      return;
    }
    const payload = {
      email: formValues.email,
      userName: formValues.firstName,
      userSurname: formValues.lastName,
      userLastname: formValues.secondLastName,
      userLanguage: formValues.language,
      userCountryCode: country.countryCode.toUpperCase(),
    };
    this.props.subscribeToNewsletter(payload);
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields((err, values) => {
      if (!err) {
        if (this.isSubmitBlockedByEmailStatus()) {
          return;
        }

        values.dateOfBirth = toSubmitDate(values.dateOfBirth);
        values.preferredLanguage = values.language
        values.brandId = this.props.brandId;
        values.isPromotions = !!values.isPromotions;
        delete values.confirmEmail;

        this.setState({ isSending: true });
        this.props.registerMember(
          tokenRegistration,
          values,
          () => {
            this.subscribeNewsletter(values);
            this.onRegisterSuccess();
          },
          this.onRegisterError
        );
      }
    });
  };

  handleChange = selectedItems => {
    this.setState({ selectedItems });
  };

  handleCountryChange = (countryId) => {
    const language = countriesLangES.indexOf(countryId) === -1 ? 'en' : 'es';
    this.props.form.setFieldsValue({ language });
  };

  renderCountryNotFoundContent = () => {
    const { lookupLoading, lookupError, onRetryLookup } = this.props;
    const t = this.context.t;

    if (lookupLoading) {
      return t('Loading countries...');
    }

    if (lookupError) {
      return (
        <span>
          {t('Could not load countries.')}{' '}
          <Btn
            className="text-main hover:text-highlight focus:outline-none p-0 inline"
            onClick={(e) => {
              if (e) {
                e.preventDefault();
                e.stopPropagation();
              }
              if (onRetryLookup) {
                onRetryLookup();
              }
            }}
          >
            {t('Try again')}
          </Btn>
        </span>
      );
    }

    return t('No countries available');
  };

  compareToEmail = (rule, value, callback) => {
    if (value && value !== this.props.form.getFieldValue('email')) {
      callback(this.context.t('The emails do not match'));
      return;
    }
    callback();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.locale !== this.props.locale && this.props.locale) {
      this.props.form.setFieldsValue({ language: this.props.locale });
    }
  }

  render() {
    const { getFieldDecorator } = this.props.form;
    const { registrationInfo } = this.props;
    const { Option } = Select;
    const urlPrivacyPolicy = this.props.locale === 'es' ? 'https://www.sandos.com/es/politica-de-privacidad' : 'https://www.sandos.com/privacy-policy';
    const urlTermsAndConditions = this.props.locale === 'es' ? 'https://www.sandos.com/es/terminos-y-condiciones-sandos4u' : 'https://www.sandos.com/terms-and-conditions-sandos4u';
    const submitDisabled = this.state.isSending || this.state.emailCheckLoading || this.isSubmitBlockedByEmailStatus();

    return (
      <div className="register-form">
        <Form className="form-line" onSubmit={this.handleSubmit} layout="vertical">
          <div className="register-form-grid">
            <Form.Item label={this.context.t('First name')} hasFeedback>
              {getFieldDecorator('firstName', {
                rules: [
                  {
                    required: true,
                    message: this.context.t('Please input your First name'),
                  },
                ],
              })(<Input size="large"/>)}
            </Form.Item>

            <Form.Item label={this.context.t('Last name')} hasFeedback>
              {getFieldDecorator('lastName', {
                rules: [
                  {
                    required: true,
                    message: this.context.t('Please input your last name'),
                  },
                ],
              })(<Input size="large"/>)}
            </Form.Item>

            <Form.Item label={this.context.t('Second last name')} hasFeedback>
              {getFieldDecorator('secondLastName', {
                rules: [
                  {
                    required: false,
                    message: this.context.t('Please input your second last name'),
                  },
                ],
              })(<Input size="large"/>)}
            </Form.Item>

            <Form.Item label={this.context.t('Country')} hasFeedback>
              {getFieldDecorator('country', {
                rules: [{
                  required: true,
                  message: this.context.t('Please enter your country'),
                }],
              })(
                <Select
                  size="large"
                  onChange={this.handleCountryChange}
                  placeholder={this.context.t('Select your country')}
                  optionFilterProp="children"
                  filterOption={filterSelectByLabel}
                  showSearch
                  notFoundContent={this.renderCountryNotFoundContent()}>
                  {(registrationInfo && registrationInfo.countryCodeTo ? registrationInfo.countryCodeTo : []).map(
                    (country) => <Option key={country.countryId}
                                         value={country.countryId}>{country.name}</Option>
                  )}
                </Select>
              )}
            </Form.Item>

            <DateOfBirthField getFieldDecorator={getFieldDecorator}
                              form={this.props.form} lang={this.props.locale} />

            <Form.Item label={this.context.t('Phone number')} hasFeedback>
              {getFieldDecorator('phoneNumber', {
                rules: [{
                  required: true,
                  message: this.context.t('Please input your phone number'),
                }],
              })(<Input size="large"/>)}
            </Form.Item>

            <Form.Item
              label={this.context.t('E-mail')}
              hasFeedback
              validateStatus={this.getEmailValidateStatus()}
              help={this.renderEmailStatusHelp()}
            >
              {getFieldDecorator('email', {
                rules: [
                  {
                    type: 'email',
                    message: this.context.t('The input is not valid E-mail!'),
                  },
                  {
                    required: true,
                    message: this.context.t('Please input your E-mail!'),
                  },
                ],
              })(
                <Input
                  size="large"
                  onChange={this.handleEmailChange}
                />
              )}
            </Form.Item>

            <Form.Item label={this.context.t('Confirm E-mail')} hasFeedback>
              {getFieldDecorator('confirmEmail', {
                rules: [
                  {
                    type: 'email',
                    message: this.context.t('The input is not valid E-mail!'),
                  },
                  {
                    required: true,
                    message: this.context.t('Please confirm your E-mail'),
                  },
                  {
                    validator: this.compareToEmail,
                  },
                ],
              })(<Input size="large" />)}
            </Form.Item>
          </div>

          <h3 className="sub-title register-prefs-title">{this.context.t('Preferences')}</h3>
          <div className="register-form-grid register-form-grid--prefs">
            <Form.Item label={this.context.t('Language')} hasFeedback>
              {getFieldDecorator('language', {
                  initialValue: this.props.locale,
                  rules: [
                    {
                      required: true,
                      message: this.context.t('Please select your language'),
                    },
                  ]
                }
              )(<Select size="large">
                  {registrationInfo && registrationInfo.languageTo && registrationInfo.languageTo.map(
                    (language) => <Option key={language.locale}
                                         value={language.locale}>{language.language}</Option>
                  )}
                </Select>
              )}
            </Form.Item>
            <Form.Item
              label={this.context.t('Hotel')}
              extra={this.context.t('If you found out about our program through one of our hotels, please select it here:')}
              hasFeedback
              className="register-hotel-item"
            >
              {getFieldDecorator('hotel', {})(<Select size="large">
                  {registrationInfo && registrationInfo.hotelsList && registrationInfo.hotelsList.map(
                    (hotel) => <Option key={hotel.hotelId}
                                       value={hotel.hotelId}>{hotel.hotelName}</Option>
                  )}
                </Select>
              )}
            </Form.Item>
          </div>

          <div className="register-form-footer">
            <div className="register-form-footer__legal">
              <Form.Item>
                {getFieldDecorator('isPromotions', {
                  valuePropName: 'checked',
                  initialValue: false,
                })(
                  <Checkbox>{this.context.t('I agree to receive promotions from {brandName}', {brandName: this.props.brandName})}</Checkbox>,
                )}
              </Form.Item>
              <Form.Item>
                {getFieldDecorator('conditions', {
                  valuePropName: 'checked',
                  rules: [
                    {
                      required: true,
                      message: this.context.t('Please check the conditions!'),
                    },
                  ],
                })(
                  <Checkbox>
                    {this.context.t('I have read and accept the')}
                    <a href={urlTermsAndConditions} target="_blank" rel="noopener noreferrer">
                      &nbsp;{this.context.t('Terms and Conditions')}
                    </a>
                    <a href={urlPrivacyPolicy} target="_blank" rel="noopener noreferrer">
                      &nbsp;{this.context.t('and Privacy Policy of {brand}', { brand: this.props.brandName })}
                    </a>
                  </Checkbox>,
                )}
              </Form.Item>
            </div>
            <div className="register-form-footer__cta">
              <Button type="primary" htmlType="submit" className="register-submit-btn"
                      disabled={submitDisabled}>
                {this.state.isSending ? this.context.t('SENDING...') : this.context.t('Register me')}
              </Button>
              <ContactAssistance />
            </div>
          </div>
        </Form>
        <ModalActivationLinkSent
          visible={this.state.showLinkSentModal}
          onClose={this.handleCloseLinkSentModal}
        />
      </div>
    );
  }
}

RegisterForm.propTypes = {
  brandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBack: PropTypes.func,
  registerMember: PropTypes.func.isRequired,
  checkRegistrationEmail: PropTypes.func.isRequired,
  resendActivationLinkByEmail: PropTypes.func.isRequired,
  showLoginModal: PropTypes.func.isRequired,
  brandName: PropTypes.string,
  onGoToForgotPassword: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  lookupLoading: PropTypes.bool,
  lookupError: PropTypes.bool,
  onRetryLookup: PropTypes.func,
};

RegisterForm.contextTypes = {
  t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  brandId: getBrandId(state),
  locale: getLanguage(state),
});

const mapDispatchToProps = (dispatch) => ({
  registerMember: (authToken, values, successCallback, failureCallback) => registerMember(authToken, values, successCallback, failureCallback)(dispatch),
  subscribeToNewsletter: (payload) => subscribeToNewsletter(payload)(dispatch),
  checkRegistrationEmail: (email) => checkRegistrationEmail(email)(dispatch),
  resendActivationLinkByEmail: (email) => resendActivationLinkByEmail(email)(dispatch),
  showLoginModal: () => dispatch(showModal('login')),
});

RegisterForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(RegisterForm);

const WrappedRegistrationForm = Form.create({ name: 'registerForm' })(withRouter(RegisterForm));
export default WrappedRegistrationForm;
